/**
 * useAuthProviderList Hook
 * Manages loading and CRUD operations for authentication providers
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/index';
import { log } from '../../utils/errors/logger';
import { useSnackbar } from '../../contexts/SnackbarContext';
import type { AuthProviderConfig } from '../../components/system/AuthProviderSettings/types';
import type { AuthProviderType } from '../../api/data-contracts';

interface UseAuthProviderListReturn {
  providers: AuthProviderConfig[];
  primaryProvider: string;
  loading: boolean;
  loadProviders: () => Promise<void>;
  deleteProvider: (provider: AuthProviderConfig) => Promise<void>;
  toggleProvider: (providerType: string, enabled: boolean) => Promise<void>;
  setPrimaryProvider: (providerType: string) => Promise<void>;
}

/**
 * Hook to manage authentication provider list and operations
 */
export const useAuthProviderList = (
  onUpdate?: () => void
): UseAuthProviderListReturn => {
  const { t } = useTranslation();
  const { showSuccess, showError, showInfo } = useSnackbar();
  const [providers, setProviders] = useState<AuthProviderConfig[]>([]);
  const [primaryProvider, setPrimaryProviderState] = useState<string>('local');
  const [loading, setLoading] = useState(false);

  const loadProviders = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiClient.getAllAuthProvidersApiAuthProvidersGet();
      setProviders(response.data || []);

      // Find primary provider
      const primary = response.data?.find((p: AuthProviderConfig) => p.is_primary);
      if (primary) {
        setPrimaryProviderState(primary.provider_type);
      }

      log.info('Loaded auth providers', { count: response.data?.length });
    } catch (error) {
      log.error('Failed to load auth providers', { error });
      showError(t('auth.providers.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t, showError]);

  const deleteProvider = useCallback(
    async (provider: AuthProviderConfig) => {
      try {
        setLoading(true);
        log.user.action('delete_auth_provider', 'AuthProviderSettings', {
          providerType: provider.provider_type,
        });

        await apiClient.deleteAuthProviderApiAuthProvidersProviderTypeDelete(
          provider.provider_type as AuthProviderType
        );

        await loadProviders();

        if (onUpdate) {
          onUpdate();
        }

        showSuccess(t('auth.providers.deleted'));
      } catch (err) {
        log.error('Failed to delete auth provider', {
          providerType: provider.provider_type,
          error: err,
        });
        showError(t('auth.providers.failedToDelete'));
      } finally {
        setLoading(false);
      }
    },
    [t, loadProviders, onUpdate, showSuccess, showError]
  );

  const toggleProvider = useCallback(
    async (providerType: string, enabled: boolean) => {
      try {
        setLoading(true);

        log.user.action('toggle_auth_provider', 'AuthProviderSettings', {
          providerType,
          enabled,
        });

        await apiClient.updateAuthProviderApiAuthProvidersProviderTypePut(
          providerType as AuthProviderType,
          {
            is_enabled: enabled,
          }
        );

        // Reload providers to get updated state
        await loadProviders();

        if (onUpdate) {
          onUpdate();
        }

        log.info('Successfully toggled auth provider', { providerType, enabled });
        showSuccess(
          enabled ? t('auth.providers.enabled') : t('auth.providers.disabled')
        );
      } catch (err) {
        log.error('Failed to toggle provider', { providerType, enabled, error: err });
        showError(
          enabled
            ? t('auth.providers.failedToEnable')
            : t('auth.providers.failedToDisable')
        );
      } finally {
        setLoading(false);
      }
    },
    [t, loadProviders, onUpdate, showSuccess, showError]
  );

  const setPrimaryProvider = useCallback(
    async (providerType: string) => {
      try {
        setLoading(true);

        log.user.action('set_primary_auth_provider', 'AuthProviderSettings', {
          providerType,
        });

        await apiClient.setPrimaryAuthProvider({
          provider_type: providerType as AuthProviderType,
        });

        // Update local state
        setPrimaryProviderState(providerType);

        if (onUpdate) {
          onUpdate();
        }

        log.info('Successfully set primary auth provider', { providerType });
        showSuccess(t('auth.providers.primaryUpdated'));
      } catch (err) {
        log.error('Failed to set primary provider', { providerType, error: err });
        showError(t('auth.providers.failedToSetPrimary'));
      } finally {
        setLoading(false);
      }
    },
    [t, onUpdate, showSuccess, showError]
  );

  return {
    providers,
    primaryProvider,
    loading,
    loadProviders,
    deleteProvider,
    toggleProvider,
    setPrimaryProvider,
  };
};
